import _extends from "@babel/runtime/helpers/extends";
import { MuiThemeProvider, createTheme as createMuiTheme, makeStyles as muiMakeStyles, createStyles as muiCreateStyles, useTheme as muiUseTheme } from "@material-ui/core/styles";
import Fade from "@material-ui/core/Fade";
import Logo from "../Logo";
import React from "react";
import classNames from "classnames";
import createBreakpoints from "./CreateBreakpoints";
// Most of the logos are text-only, so make them a consistent height
// This is set manually on logos that are more complex
// Any logo that is more than 1em tall should set a margin-top such that the text part of the logo is centered
const logoStyle = {
  maxHeight: "1em"
};
export const scrollbars = {
  main: {
    // Enable overflow
    overflow: "auto",
    // Webkit/Blink (Chrome/Safari)
    overflowX: "overlay",
    overflowY: "overlay",
    "&::-webkit-scrollbar": {
      width: "0.5rem"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.25)"
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.18)"
    },
    "&::-webkit-scrollbar-thumb:active": {
      backgroundColor: "rgba(0, 0, 0, 0.21)"
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgba(0, 0, 0, 0.05)"
    },
    // Firefox
    scrollbarColor: "rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.05)",
    scrollbarWidth: "thin"
  }
};
const defaultTheme = {
  name: "CivicPlus",
  logo: /*#__PURE__*/React.createElement(Logo, {
    product: "CivicPlus",
    white: true
    // Each logo needs different values
    // eslint-disable-next-line no-inline-styles/no-inline-styles
    ,
    style: {
      maxHeight: "1.22em",
      marginTop: ".22em"
    },
    preserveAspectRatio: "xMinYMid meet"
  }),
  palette: {
    contrastThreshold: 4.5,
    primary: {
      main: "#CC0020"
    },
    secondary: {
      main: "#383838"
    },
    background: {
      blurred: "rgba(255,255,255,.75)",
      default: "#eee"
    },
    text: {
      secondary: `rgba(0,0,0,.6)`
    }
  }
};
const makeActionColorFromHex = hex => {
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
  return `rgba(${r},${g},${b},.07)`;
};
const themes = {
  default: defaultTheme,
  clerk: {
    name: "CivicClerk",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicClerk",
      white: true,
      style: logoStyle,
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#7958E8"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  engageCentral: {
    name: "CivicEngage Central",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicEngage Central",
      white: true,
      style: logoStyle,
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#006CF6"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  engageEvolve: {
    name: "CivicEngage Evolve",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicEngage Evolve",
      white: true,
      style: logoStyle,
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#006CF6"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  hr: {
    name: "CivicHR",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicHR",
      white: true,
      style: logoStyle,
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#007EBC"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  optimize: {
    name: "CivicOptimize",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicOptimize",
      white: true,
      style: logoStyle,
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#008672"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  ready: {
    name: "CivicReady",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicReady",
      white: true
      // Each logo needs different values
      // eslint-disable-next-line no-inline-styles/no-inline-styles
      ,
      style: {
        maxHeight: "1.61em",
        marginTop: ".61em"
      },
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#BE4B00"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  rec: {
    name: "CivicRec",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicRec",
      white: true,
      style: logoStyle,
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#408100"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  service: {
    name: "CivicService",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "CivicService",
      white: true
      // Each logo needs different values
      // eslint-disable-next-line no-inline-styles/no-inline-styles
      ,
      style: {
        maxHeight: "1.5em",
        marginTop: ".5em"
      },
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#737373"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  scf: {
    name: "SeeClickFix",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "SeeClickFix",
      white: true
      // Each logo needs different values
      // eslint-disable-next-line no-inline-styles/no-inline-styles
      ,
      style: {
        maxHeight: "1.46em",
        marginTop: ".46em"
      },
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#0099CC"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  preamble: {
    name: "Preamble",
    logo: /*#__PURE__*/React.createElement(Logo, {
      product: "Preamble",
      white: true
      // Each logo needs different values
      // eslint-disable-next-line no-inline-styles/no-inline-styles
      ,
      style: {
        maxHeight: "1.46em",
        marginTop: ".46em"
      },
      preserveAspectRatio: "xMinYMid meet"
    }),
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#3A7470"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  }
};
export { themes };
export const getProductName = key => {
  if (key && key in themes) return themes[key].name;
  return "Unknown product";
};
export const createTheme = (product, primaryHex, title, cssReset, themeOverride, maxBreakpoint) => {
  const themePalette = product && product in themes ? JSON.parse(JSON.stringify(themes[product].palette)) : JSON.parse(JSON.stringify(themes.default.palette));
  let otherTypographyDefaults = {};
  let otherOverride = {};
  if (cssReset) {
    otherTypographyDefaults = {
      h1: {
        fontSize: "96px"
      },
      h2: {
        fontSize: "60px"
      },
      h3: {
        fontSize: "48px"
      },
      h4: {
        fontSize: "34px"
      },
      h5: {
        fontSize: "25px"
      },
      h6: {
        fontSize: "20px"
      },
      subtitle1: {
        fontSize: "16px"
      },
      subtitle2: {
        fontSize: "14px"
      },
      body1: {
        fontSize: "16px"
      },
      body2: {
        fontSize: "14px"
      },
      caption: {
        fontSize: "12px"
      },
      overline: {
        fontSize: "12px"
      }
    };
    otherOverride = {
      MuiSvgIcon: {
        root: {
          fontSize: "24px"
        },
        fontSizeSmall: {
          fontSize: "20px"
        },
        fontSizeLarge: {
          fontSize: "35px"
        }
      },
      MuiInputBase: {
        input: {
          height: "inherit"
        }
      }
    };
  }
  if (typeof primaryHex !== "undefined" && primaryHex !== "") {
    themePalette.primary.main = primaryHex;
  }
  themeOverride = themeOverride || {};
  const defaultTheme = createMuiTheme();
  if (maxBreakpoint) {
    const theme = themeOverride?.breakpoints ? themeOverride : defaultTheme;
    if (theme.breakpoints) {
      const customBreakpoints = breakpointCappedToSize(theme.breakpoints, maxBreakpoint);
      themeOverride.breakpoints = {
        values: customBreakpoints
      };
    }
  }
  const containerBreakpoints = createBreakpoints({
    values: themeOverride.breakpoints?.values || defaultTheme.breakpoints.values,
    queryName: "@container"
  });
  const actionColor = makeActionColorFromHex(themePalette.primary.main);
  themePalette.action = {
    ...themePalette.action,
    selected: actionColor,
    hover: actionColor
  };
  return {
    ...createMuiTheme({
      ...themeOverride,
      palette: themePalette,
      typography: {
        fontFamily: "'Lato', 'Helvetica', 'Arial', sans-serif",
        button: {
          fontSize: ".75rem",
          letterSpacing: 1,
          wordBreak: "break-word"
        },
        ...otherTypographyDefaults
      },
      overrides: {
        MuiInputBase: {
          input: {
            height: "auto"
          }
        },
        MuiPickersToolbar: {
          toolbar: {
            backgroundColor: themePalette.primary.main
          }
        },
        MuiPickersModal: {
          dialogRoot: {
            borderRadius: 0
          }
        },
        ...themeOverride?.overrides,
        ...otherOverride
      }
    }),
    scrollbars,
    title,
    containerBreakpoints
  };
};
const routeTransitionStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexGrow: 1,
    flexDirection: "column"
  }
}));
export const RouteTransitionProvider = ({
  children,
  className,
  ...otherProps
}) => {
  const classes = routeTransitionStyles();
  return /*#__PURE__*/React.createElement(Fade, {
    in: true
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: classNames("prmbl-transition-provider", className, classes.root)
  }, otherProps), children));
};
export const StyleWithOverrides = props => {
  const {
    theme,
    overrides,
    children
  } = props;
  const renderedTheme = {
    ...theme,
    overrides: {
      ...theme.overrides,
      ...overrides
    }
  };
  return /*#__PURE__*/React.createElement(MuiThemeProvider, {
    theme: renderedTheme
  }, children);
};

// Most of this interface comes from MUI, just swapped the default theme to PreambleTheme
// eslint-disable-next-line @typescript-eslint/ban-types
export function makeStyles(styles, options) {
  return muiMakeStyles(styles, options);
}

// Most of this interface comes from MUI, just swapped the default theme to PreambleTheme
// eslint-disable-next-line @typescript-eslint/ban-types
export function createStyles(styles) {
  return muiCreateStyles(styles);
}
export const useTheme = () => {
  return muiUseTheme();
};
export const breakpointCappedToSize = (breakpoint, size) => {
  const customBreakpoints = breakpoint.values;
  const length = Object.keys(customBreakpoints).length;
  const index = Object.keys(customBreakpoints).indexOf(size);
  const positionsUpdate = length - index - 1;
  Object.entries(customBreakpoints).forEach(([key], index) => {
    const newPosition = index + positionsUpdate;
    const newValue = Object.values(customBreakpoints)[newPosition];
    if (newValue) {
      customBreakpoints[key] = newValue;
    } else {
      customBreakpoints[key] = 99999; // A size that will never be reached
    }
  });

  customBreakpoints["xs"] = 0; // Extra small size has to be always 0, otherwise the content disappears when it hit below that size
  return customBreakpoints;
};